/**
 * TrackIndex Component
 *
 * Displays all tracks for a cycle as a list, following the same UI pattern
 * as the Cycles list (rows, Open button, actions menu, double-click rename).
 * Per spec: Each track defines a comparability lane partitioned by scope × category × altitude.
 */

import { useUserStore } from '../../stores/prioritization/userStore';
import { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Layers,
  CheckCircle2,
  Circle,
  AlertCircle,
  Loader2,
  X,
  Unlink,
  Link2,
  Play,
  MoreVertical,
  Archive,
} from 'lucide-react';
import { detectTrackCycleConflicts, type BindingConflict } from '@/lib/bindingConflictDetector';
import { BindingConflictReview } from '@/components/prioritization/BindingConflictReview';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { getTrackCellLabel, isDefaultTrack, TRACK_STATUS_LABEL } from '@/types/prioritization/track';
import type { Track } from '@/types/prioritization/track';
import { TrackOwnerSubControl } from '@/components/prioritization/TrackOwnerSubControl';
import { CreateTrackModal } from '@/components/prioritization/tracks/CreateTrackModal';
import { btnPrimary, btnSecondary, heading, status } from '@/components/mono/primitives';
import { ReverseActionButton } from '@/components/reversals/ReverseActionButton';
import { Button } from '@/components/ui/Button';
import { unbindTrackFromCycle } from '@/lib/reversals/reversalsService';
import {
  CREATE_TRACK_LABEL,
  ATTACH_TRACK_LABEL,
  BIND_TRACK_DIALOG_TITLE,
  BIND_TRACK_CONFIRM_LABEL,
  BIND_TRACK_REVIEW_TITLE,
} from '@/lib/tracks/vocabulary';

interface TrackIndexProps {
  cycleId: string;
  cycleName: string;
  onCreateTrack?: () => void;
  /** When true, uses setup-wizard padding and typography (e.g. CycleSetupWizard). */
  embedded?: boolean;
}

// ── Track concept strip ──────────────────────────────────────────────────────
// Flat left-accented aside — never a card. Track rows own the card treatment.

const FRAME_CONCEPTS = [
  {
    key: 'category',
    label: 'Item category',
    pillClass:
      'border border-[var(--rule-bright)] bg-[var(--slate-dim)] text-[var(--text-sec)] hover:bg-[var(--hover-lift)]',
    dotClass: 'bg-[var(--slate)]',
    activeClass: 'ring-2 ring-[color:var(--slate)]',
    tagline: 'What kind of items can be compared in this track.',
    body: 'Item category is the semantic kind of work being compared — for example Problem, Solution, Opportunity, or Risk. Everything in the same Track shares one category so comparisons stay apples-to-apples.',
  },
  {
    key: 'altitude',
    label: 'Altitude',
    pillClass:
      'border border-[var(--rule-bright)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-lift)]',
    dotClass: 'bg-[var(--green)]',
    activeClass: 'ring-2 ring-[color:var(--green)]',
    tagline: 'How strategic the items are — tactical through enterprise.',
    body: 'Altitude is the strategic level (typically 1 = tactical through 5 = enterprise-strategic). Items must match on category and altitude to belong in the same track — you don\'t rank tactical hygiene next to portfolio-level bets.',
  },
  {
    key: 'scope',
    label: 'Scope',
    pillClass:
      'border border-[var(--rule-bright)] bg-[var(--mustard-dim)] text-[var(--mustard)] hover:bg-[var(--hover-lift)]',
    dotClass: 'bg-[var(--mustard)]',
    activeClass: 'ring-2 ring-[color:var(--mustard)]',
    tagline: 'Which part of the organization owns this lane.',
    body: 'Scope is the org unit that owns this Track — team, department, or division. Two tracks can share the same category and altitude but differ in scope, giving parallel lanes that each stay internally comparable.',
  },
] as const;

type FrameConceptKey = (typeof FRAME_CONCEPTS)[number]['key'];

function TrackBanner() {
  const [activeKey, setActiveKey] = useState<FrameConceptKey | null>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const openConcept = useCallback((key: FrameConceptKey) => {
    if (activeKey === key) { setActiveKey(null); return; }
    const btn = btnRefs.current[key];
    if (!btn) { setActiveKey(key); return; }
    const rect = btn.getBoundingClientRect();
    const popW = 320;
    let left = rect.left + rect.width / 2 - popW / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - popW - 12));
    setPopoverPos({ top: rect.bottom + 8, left });
    setActiveKey(key);
  }, [activeKey]);

  useEffect(() => {
    if (!activeKey) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (Object.values(btnRefs.current).some(b => b?.contains(target))) return;
      setActiveKey(null);
    };
    window.addEventListener('mousedown', close);
    const onScroll = () => setActiveKey(null);
    window.addEventListener('scroll', onScroll, true);
    const onResize = () => setActiveKey(null);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [activeKey]);

  const activeConcept = FRAME_CONCEPTS.find(c => c.key === activeKey) ?? null;

  const popover = activeConcept
    ? createPortal(
        <div
          ref={popoverRef}
          role="tooltip"
          className="fixed z-[100] w-[320px] border border-[var(--rule)] bg-[var(--bg-raised)] p-4"
          style={{ top: popoverPos.top, left: popoverPos.left }}
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex h-6 w-6 items-center justify-center border text-[11px] font-bold ${activeConcept.pillClass}`}
              >
                {activeConcept.key === 'category' ? 'C' : activeConcept.key === 'altitude' ? 'A' : 'S'}
              </span>
              <span className="text-sm font-semibold text-[var(--text-pri)]">{activeConcept.label}</span>
            </div>
            <button type="button" onClick={() => setActiveKey(null)} className="p-0.5 text-[var(--text-dim)] hover:text-[var(--text-ter)] flex-shrink-0" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs font-medium mb-2 leading-relaxed text-[var(--text-sec)]">{activeConcept.tagline}</p>
          <p className="text-xs text-[var(--text-ter)] leading-relaxed">{activeConcept.body}</p>
        </div>,
        document.body,
      )
    : null;

  return (
    // Instructional strip — deliberately NOT a card. Track rows use solid
    // + --surface; this stays flat and left-accented so it never
    // reads as another Track in the list.
    <aside
      className="relative border-l-2 py-2 pl-3.5 pr-1"
      style={{ borderColor: 'var(--mustard)' }}
      aria-label="How Tracks work"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-3 min-w-0">
        <p className="flex-shrink-0 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--mustard)]">
          How tracks work
        </p>
        <p className="text-sm leading-relaxed text-[var(--text-ter)] min-w-0">
          Defined by{' '}
          {FRAME_CONCEPTS.map((concept, i) => (
            <span key={concept.key}>
              {i > 0 && (i === FRAME_CONCEPTS.length - 1 ? ', and ' : ', ')}
              <button
                ref={el => { btnRefs.current[concept.key] = el; }}
                type="button"
                onClick={() => openConcept(concept.key)}
                className={`font-medium underline decoration-dotted underline-offset-2 transition-colors ${
                  activeKey === concept.key
                    ? 'text-[var(--mustard)] decoration-solid'
                    : 'text-[var(--text-sec)] hover:text-[var(--mustard)]'
                }`}
                aria-pressed={activeKey === concept.key}
              >
                {concept.label.toLowerCase()}
              </button>
            </span>
          ))}
          {' '}— who compares what, at what strategic level.
        </p>
      </div>
      {popover}
    </aside>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

/**
 * Track status is READ, never pressed, so it uses the borderless `status`
 * readout rather than a filled pill — a pill is the button's affordance.
 * Tones: slate = in progress, green = settled, coral = rejected, neutral = gone.
 */
function getStatusBadge(track: Track) {
  switch (track.status) {
    case 'accepted':
      return (
        <span {...status('green')}>
          <CheckCircle2 className="h-3.5 w-3.5" />{TRACK_STATUS_LABEL.accepted}
        </span>
      );
    case 'final_rejected':
      return (
        <span {...status('coral')}>
          <AlertCircle className="h-3.5 w-3.5" />{TRACK_STATUS_LABEL.final_rejected}
        </span>
      );
    case 'archived':
      return (
        <span {...status('neutral')}>
          <Circle className="h-3.5 w-3.5" />{TRACK_STATUS_LABEL.archived}
        </span>
      );
    default:
      return (
        <span {...status('slate')}>
          <Circle className="h-3.5 w-3.5" />{TRACK_STATUS_LABEL.active}
        </span>
      );
  }
}

/** The × between the factors of a Track. Same ink as the chips it separates. */
const metaSepClass = 'px-0.5 font-mono text-[0.8rem] text-[var(--text-ter)]';

const metaChipClass =
  'inline-flex items-center border px-2.5 py-1 font-mono text-[0.74rem] uppercase tracking-[0.08em] whitespace-nowrap';

function FrameMetaChips({ track }: { track: Track }) {
  const cellLabel = getTrackCellLabel(track);
  const [category, altitude] = cellLabel.split(' × ');
  const isDefault = isDefaultTrack(track);

  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <span className={`${metaChipClass} bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--rule-bright)]`}>
        {category}
      </span>
      <span className={metaSepClass} aria-hidden>×</span>
      <span className={`${metaChipClass} bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--rule-bright)]`}>
        {altitude}
      </span>
      {track.scopeLabel && (
        <>
          <span className={metaSepClass} aria-hidden>×</span>
          <span className={`${metaChipClass} bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--rule-bright)]`}>
            {track.scopeLabel}
          </span>
        </>
      )}
      {isDefault && (
        <span className={`${metaChipClass} bg-[var(--surface-raised)] text-[var(--text-ter)] border-[var(--rule)]`}>
          Default lane
        </span>
      )}
    </div>
  );
}

// ── Row action menu ───────────────────────────────────────────────────────────

function TrackRowActions({ track, onArchive, canArchive, cycleName, onUnbound }: {
  track: Track;
  onArchive: () => void;
  /** Workspace admin — archiving hides the track for everyone. */
  canArchive: boolean;
  cycleName: string;
  /** Refetch after a successful unbind. */
  onUnbound: () => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuWidth = 152;

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const menuHeight = 112;
    let top = anchor.bottom + 4;
    if (top + menuHeight > window.innerHeight - 8) top = Math.max(8, anchor.top - menuHeight - 4);
    let left = anchor.right - menuWidth;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    setMenuPos({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('resize', close); window.removeEventListener('scroll', close, true); };
  }, [open]);

  const menuPortal = open && typeof document !== 'undefined' && createPortal(
    <>
      <div className="fixed inset-0 z-[80]" aria-hidden onClick={() => setOpen(false)} />
      <div
        role="menu"
        className="fixed z-[90] w-38 bg-[var(--bg-raised)] border border-[var(--rule)] py-1"
        style={{ top: menuPos.top, left: menuPos.left, minWidth: menuWidth }}
      >
        <button
          type="button"
          onClick={() => { navigate(`/prioritization/tracks/${track.id}`); setOpen(false); }}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-sec)] hover:bg-[var(--surface)]"
        >
          <Play className="h-3.5 w-3.5" />Open
        </button>
        {track.status !== 'archived' && canArchive && (
          <button
            type="button"
            onClick={() => { onArchive(); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--text-sec)] hover:bg-[var(--surface)]"
          >
            <Archive className="h-3.5 w-3.5" />Archive
          </button>
        )}
        {/* The red "Delete" item that used to sit here called archiveTrack — the
            same function as Archive above it. It destroyed nothing; it just wore
            a trash icon and red text. Two entries for one action, one of them
            lying about severity. */}
        {canArchive && (
          <div onClick={() => setOpen(false)}>
            <ReverseActionButton
              label="Unbind from cycle"
              confirmTitle="Unbind from cycle"
              explain={`This will unbind "${track.name}" from "${cycleName}", returning it to preparatory mode. Governance capabilities (proposals, catchball) will be unavailable until it is bound to a cycle again.`}
              run={(reason) => unbindTrackFromCycle(track.id, reason)}
              onDone={onUnbound}
              variant="ghost"
              className="!w-full !justify-start !gap-2 !px-3 !py-2 !text-xs !font-normal text-[var(--text-sec)]"
            />
          </div>
        )}
      </div>
    </>,
    document.body,
  );

  return (
    <div className="flex items-center gap-1 justify-end flex-shrink-0" onClick={e => e.stopPropagation()}>
      <div className="inline-flex items-center border bg-[var(--surface)]" style={{ borderColor: 'var(--rule-bright)' }}>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); navigate(`/prioritization/tracks/${track.id}?tab=sessions`); }}
          className="inline-flex h-8 items-center gap-1.5 px-3 font-mono text-[0.72rem] uppercase tracking-[0.08em] text-[var(--text-ter)] transition-colors hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)]"
          title="Open Sessions tab"
        >
          <Play className="h-3 w-3" />Session
        </button>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); navigate(`/prioritization/tracks/${track.id}`); }}
          className="inline-flex h-8 items-center gap-1.5 border-l px-3 font-mono text-[0.72rem] uppercase tracking-[0.08em] text-[var(--text-pri)] transition-colors hover:bg-[var(--hover-tint)]"
          style={{ borderColor: 'var(--rule)' }}
        >
          Open
        </button>
      </div>
      <button
        ref={anchorRef}
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        className="p-1.5 hover:bg-[var(--surface-raised)] transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Track actions"
      >
        <MoreVertical className="h-3.5 w-3.5 text-[var(--text-dim)]" />
      </button>
      {menuPortal}
    </div>
  );
}

// ── Inline-editable track name ────────────────────────────────────────────────

function TrackNameCell({ track, onStartEdit, isEditing, editingName, setEditingName, onCommit, onCancel, inputRef }: {
  track: Track;
  onStartEdit: (e: React.MouseEvent) => void;
  isEditing: boolean;
  editingName: string;
  setEditingName: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <Layers className="h-4 w-4 shrink-0 text-primary-500" />
        {isEditing ? (
          <input
            ref={inputRef}
            value={editingName}
            onChange={e => setEditingName(e.target.value)}
            onBlur={onCommit}
            onKeyDown={e => { if (e.key === 'Enter') onCommit(); if (e.key === 'Escape') onCancel(); }}
            className="text-sm font-semibold text-[var(--text-pri)] border border-primary-400 px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary-400/40 bg-[var(--bg-raised)] min-w-0 max-w-[260px]"
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className="text-sm font-semibold text-[var(--text-pri)] hover:text-primary-600 truncate cursor-pointer"
            onDoubleClick={onStartEdit}
            title="Double-click to rename"
          >
            {track.name}
          </span>
        )}
        {getStatusBadge(track)}
      </div>
      <FrameMetaChips track={track} />
      {track.description && (
        <p className="mt-1.5 text-xs text-[var(--text-ter)] line-clamp-2">
          {track.description}
        </p>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function TrackIndex({ cycleId, cycleName, onCreateTrack, embedded = false }: TrackIndexProps) {
  const navigate = useNavigate();

  const tracks = useTrackStore(state => state.tracks);
  const isLoading = useTrackStore(state => state.isLoading);
  const error = useTrackStore(state => state.error);
  const fetchTracksForCycle = useTrackStore(state => state.fetchTracksForCycle);
  const bindTrackToCycle = useTrackStore(state => state.bindTrackToCycle);
  const archiveTrack = useTrackStore(state => state.archiveTrack);
  // Archiving hides a track for everyone in the workspace but destroys nothing
  // and can be undone, so it sits with the people who run the work rather than
  // behind the admin gate. (Permanent deletion is the one that needs
  // `admin.settings` — see ProcessList.)
  const canDo = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canArchiveTrack = canDo('process.edit');
  const clearError = useTrackStore(state => state.clearError);
  const updateTrack = useTrackStore(state => state.updateTrack);

  const cycles = useDegapProcessStore(state => state.cycles);
  const currentCycle = useMemo(() => cycles.find(c => c.id === cycleId), [cycles, cycleId]);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachTargetTrackId, setAttachTargetTrackId] = useState('');
  const [isAttaching, setIsAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [attachConflicts, setAttachConflicts] = useState<BindingConflict[]>([]);
  const [attachStep, setAttachStep] = useState<'select' | 'review'>('select');
  // Next step after a successful bind: say what happened and offer the track.
  const [boundNotice, setBoundNotice] = useState<{ id: string; name: string } | null>(null);

  // ── Inline rename ──────────────────────────────────────────────────────────
  const [editingTrackId, setEditingTrackId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  const startEditingTrack = useCallback((track: Track, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditingTrackId(track.id);
    setEditingName(track.name);
  }, []);

  const commitTrackRename = useCallback(async () => {
    if (!editingTrackId || !editingName.trim()) { setEditingTrackId(null); return; }
    await updateTrack(editingTrackId, { name: editingName.trim() });
    setEditingTrackId(null);
  }, [editingTrackId, editingName, updateTrack]);

  useEffect(() => {
    if (editingTrackId && editInputRef.current) editInputRef.current.select();
  }, [editingTrackId]);

  // Archived tracks drop out of view, matching standaloneTracks below — without
  // this, archiving a cycle's track left the row exactly where it was.
  const cycleTracks = useMemo(
    () => tracks.filter(t => t.cycleId === cycleId && t.status !== 'archived'),
    [tracks, cycleId],
  );
  const standaloneTracks = useMemo(() => tracks.filter(t => t.isStandalone && t.status !== 'archived'), [tracks]);

  useEffect(() => {
    if (cycleId) fetchTracksForCycle(cycleId);
  }, [cycleId, fetchTracksForCycle]);

  const handleCreateClick = () => {
    if (onCreateTrack) onCreateTrack();
    else setShowCreateModal(true);
  };

  const handleAttachTrack = async () => {
    if (!attachTargetTrackId || isAttaching) return;
    setIsAttaching(true);
    setAttachError(null);
    const boundName = standaloneTracks.find(t => t.id === attachTargetTrackId)?.name ?? 'The track';
    const boundId = attachTargetTrackId;
    const ok = await bindTrackToCycle(attachTargetTrackId, cycleId);
    setIsAttaching(false);
    if (ok) {
      setBoundNotice({ id: boundId, name: boundName });
      setShowAttachModal(false);
      setAttachTargetTrackId('');
      setAttachStep('select');
      setAttachConflicts([]);
      await fetchTracksForCycle(cycleId);
    } else {
      const storeError = useTrackStore.getState().error;
      setAttachError(storeError ?? 'The track could not be bound. Please try again.');
    }
  };

  const handleAttachReview = () => {
    if (!attachTargetTrackId || !currentCycle) return;
    const selectedTrack = standaloneTracks.find(t => t.id === attachTargetTrackId);
    if (!selectedTrack) return;
    const detected = detectTrackCycleConflicts(selectedTrack, currentCycle);
    if (detected.length > 0) { setAttachConflicts(detected); setAttachStep('review'); }
    else handleAttachTrack();
  };

  // This archives; it does not delete. The prompt used to say "This cannot be
  // undone", which was false in both directions — nothing was destroyed, and the
  // track can be brought back. Telling someone an action is irreversible when it
  // isn't makes them avoid a safe action, and trains them to distrust the ones
  // that really are irreversible.
  const handleArchive = (track: Track) => {
    if (confirm(`Archive "${track.name}"? It will be hidden from this cycle. Nothing is deleted.`)) {
      archiveTrack(track.id);
    }
  };

  if (isLoading && cycleTracks.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-[var(--text-dim)] animate-spin" />
      </div>
    );
  }

  return (
    <div className={`space-y-5 ${embedded ? 'p-5 sm:p-6' : ''}`}>
      {boundNotice && (
        <div role="status" className="flex items-center gap-3 p-3 border border-[var(--rule)] bg-[var(--surface)] text-sm text-[var(--text-sec)]">
          <span className="flex-1">{boundNotice.name} is now bound to this cycle. Its sessions are cycle-linked.</span>
          <Button variant="ghost" size="sm" onClick={() => navigate(`/prioritization/tracks/${boundNotice.id}`)}>
            Open track
          </Button>
          <Button variant="ghost" size="sm" aria-label="Dismiss" onClick={() => setBoundNotice(null)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 {...heading()}>
            Tracks
          </h3>
          <p className="mt-1 text-sm text-[var(--text-sec)]">
            {cycleTracks.length} track{cycleTracks.length !== 1 ? 's' : ''} in this cycle
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {standaloneTracks.length > 0 && (
            <button
              onClick={() => setShowAttachModal(true)}
              {...btnSecondary()}
            >
              <Link2 className="h-4 w-4" />{ATTACH_TRACK_LABEL}
            </button>
          )}
          <button
            onClick={handleCreateClick}
            {...btnPrimary()}
          >
            <Plus className="h-4 w-4" />{CREATE_TRACK_LABEL}
          </button>
        </div>
      </div>

      <TrackBanner />

      {error && (
        <div className="p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={clearError}><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Empty state */}
      {cycleTracks.length === 0 && (
        <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)]/80 p-8 text-center">
          <Layers className="h-12 w-12 text-[var(--text-dim)] mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-1">No Tracks yet</h3>
          <p className="text-sm text-[var(--text-ter)] mb-4 max-w-md mx-auto">
            Create a Track to define the bounded context for prioritization.
            {standaloneTracks.length > 0 && ' Or bind a standalone track you already set up.'}
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={handleCreateClick}
              {...btnPrimary()}
            >
              <Plus className="h-4 w-4 mr-1" />{CREATE_TRACK_LABEL}
            </button>
            {standaloneTracks.length > 0 && (
              <button
                onClick={() => setShowAttachModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] hover:bg-[var(--mustard-dim)] transition-colors"
              >
                <Link2 className="h-4 w-4" />{ATTACH_TRACK_LABEL}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Track cards */}
      {cycleTracks.length > 0 && (
        <div className="space-y-3">
          {cycleTracks.map(track => (
            <div
              key={track.id}
              onClick={() => { if (editingTrackId !== track.id) navigate(`/prioritization/tracks/${track.id}`); }}
              className="group border border-[var(--rule)] bg-[var(--bg-raised)] p-4 cursor-pointer transition-colors hover:bg-[var(--hover-tint)]"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <TrackNameCell
                  track={track}
                  onStartEdit={e => startEditingTrack(track, e)}
                  isEditing={editingTrackId === track.id}
                  editingName={editingName}
                  setEditingName={setEditingName}
                  onCommit={commitTrackRename}
                  onCancel={() => setEditingTrackId(null)}
                  inputRef={editInputRef}
                />
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 lg:gap-4 flex-shrink-0 border-t border-[var(--rule)] pt-3 lg:border-t-0 lg:pt-0">
                  <div onClick={e => e.stopPropagation()}>
                    <TrackOwnerSubControl trackId={track.id} variant="surface" workspaceId={currentWorkspaceId} />
                  </div>
                  <TrackRowActions
                    track={track}
                    onArchive={() => handleArchive(track)}
                    canArchive={canArchiveTrack}
                    cycleName={cycleName}
                    onUnbound={() => fetchTracksForCycle(cycleId)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Attach Existing Track Modal */}
      {showAttachModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            if (!isAttaching) { setShowAttachModal(false); setAttachError(null); setAttachTargetTrackId(''); setAttachStep('select'); setAttachConflicts([]); }
          }} />
          <div className="relative bg-[var(--bg-raised)] max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
                <Link2 className="h-5 w-5 text-[var(--mustard)]" />
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-pri)]">
                {attachStep === 'review' ? BIND_TRACK_REVIEW_TITLE : BIND_TRACK_DIALOG_TITLE}
              </h3>
              <button onClick={() => { if (!isAttaching) { setShowAttachModal(false); setAttachError(null); setAttachTargetTrackId(''); setAttachStep('select'); setAttachConflicts([]); } }} className="ml-auto text-[var(--text-dim)] hover:text-[var(--text-ter)]">
                <X className="h-5 w-5" />
              </button>
            </div>

            {attachStep === 'select' ? (
              <>
                <p className="text-sm text-[var(--text-ter)] mb-5 pl-12">
                  Select a standalone Track to bind to this cycle.
                </p>
                {attachError && (
                  <div className="mb-4 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">{attachError}</div>
                )}
                <div className="mb-5 space-y-2">
                  {standaloneTracks.map(t => (
                    <label
                      key={t.id}
                      className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${attachTargetTrackId === t.id ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'}`}
                    >
                      <input type="radio" name="attachTrack" value={t.id} checked={attachTargetTrackId === t.id} onChange={() => setAttachTargetTrackId(t.id)} className="mt-0.5 accent-amber-600" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[var(--text-pri)]">{t.name}</span>
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-1.5 py-0.5">
                            <Unlink className="h-2.5 w-2.5" />Unbound
                          </span>
                        </div>
                        {t.description && <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{t.description}</p>}
                      </div>
                    </label>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button onClick={() => { setShowAttachModal(false); setAttachError(null); setAttachTargetTrackId(''); setAttachStep('select'); setAttachConflicts([]); }} className="flex-1 px-4 py-2 text-[var(--text-sec)] bg-[var(--surface-raised)] hover:bg-[var(--hover-lift)] transition-colors" disabled={isAttaching}>Cancel</button>
                  <button onClick={handleAttachReview} disabled={!attachTargetTrackId || isAttaching} className="flex-1 px-4 py-2 text-white bg-[var(--mustard)] hover:bg-[var(--mustard)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
                    {isAttaching ? <><Loader2 className="h-4 w-4 animate-spin" />Binding…</> : <><Link2 className="h-4 w-4" />Continue</>}
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-3">
                <BindingConflictReview
                  bindingLabel={`Attaching "${standaloneTracks.find(t => t.id === attachTargetTrackId)?.name ?? 'track'}" to this cycle`}
                  conflicts={attachConflicts}
                  onAccept={handleAttachTrack}
                  onCancel={() => { setAttachStep('select'); setAttachConflicts([]); setAttachError(null); }}
                  isAccepting={isAttaching}
                  error={attachError}
                  acceptLabel={BIND_TRACK_CONFIRM_LABEL}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <CreateTrackModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        cycleId={cycleId}
        cycleName={cycleName}
        onTrackCreated={(track) => {
          void fetchTracksForCycle(cycleId);
          // Next step: the new track's Defaults tab, where its item scope is set.
          navigate(`/prioritization/tracks/${track.id}?tab=defaults`);
        }}
      />
    </div>
  );
}

export default TrackIndex;
